import type { GetStaticPaths, GetStaticProps } from 'next'
import { getSiteConfiguration } from '@magal/services/sanity-service'
import {
  getAllShopifyCollectionPaths,
  getShopifyCollection,
} from '@magal/services/shopify-service'
import { PageBuilderSection, PageResponse } from '@magal/models'
import { DefaultPage } from '@magal/page'
import { getCollectionPageByHandle } from '@magal/services/sanity-service'
import { captureException, withScope } from '@sentry/nextjs'
import {
  REVALIDATE_COLLECTION,
  REVALIDATE_NOT_FOUND,
  ROUTE_BASE_COLLECTIONS,
} from '@magal/configs'
import { fetchServiceWithRetry, logDebugMessage } from '@magal/utils'

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: string[] = await getAllShopifyCollectionPaths()
  return {
    paths,
    fallback: 'blocking',
  }
}

type PageProps = Omit<
  PageResponse<PageBuilderSection[]>,
  'siteConfiguration'
> | null

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ params, locale, preview = false }) => {
  const { handle } = params

  if (Array.isArray(handle)) {
    throw new Error('handle must be passed as string. Received array')
  }

  const pageProps: PageResponse<PageBuilderSection[]> | undefined =
    await (async () => {
      // 1. Fetch collection from Shopify
      const shopifyRes = await fetchServiceWithRetry(
        () => getShopifyCollection(handle, locale),
        5,
      )
      if (shopifyRes.status === 'ERROR') {
        const exception = new Error(
          `[PLP] Shopify function error ${JSON.stringify(shopifyRes.errors)}`,
        )
        captureException(exception)
        throw exception
      }
      if (!shopifyRes.data) {
        return undefined
      }
      const shopifyCollection = shopifyRes.data

      // 2. Fetch collection from Sanity
      const sanityRes = await fetchServiceWithRetry(
        () => getCollectionPageByHandle(handle, locale),
        5,
      )

      if (sanityRes.status === 'ERROR') {
        const exception = new Error(
          `[PLP] Sanity function error ${JSON.stringify(sanityRes.errors)}`,
        )
        captureException(exception)
        throw exception
      }
      const sanityCollectionData = sanityRes.data

      const BASE_PAGE_PROPS: Omit<PageProps, 'pageBuilder'> = {
        seo: {
          metaTitle: shopifyCollection.seo.title ?? shopifyCollection.title,
          metaDescription: shopifyCollection.seo.description,
          ogTitle: shopifyCollection.seo.title ?? shopifyCollection.title,
          ogDescription: shopifyCollection.seo.description,
        },
        shopifyData: {
          collection: shopifyCollection,
        },
        showIcons: true,
      }

      const BASE_SECTION_COLLECTION_SCHEMA = {
        _type: 'sectionCollection',
        _key: `sectionCollection-${handle}`,
        collection: shopifyCollection,
      } as const

      if (sanityCollectionData) {
        const { promoTiles, pageBuilder, promoBanner } = sanityCollectionData

        return {
          pageBuilder: [
            {
              ...BASE_SECTION_COLLECTION_SCHEMA,
              promoTiles,
              promoBanner,
            },
            ...pageBuilder,
          ],
          ...BASE_PAGE_PROPS,
        }
      } else {
        logDebugMessage(`Missing Sanity collection data for: ${handle}`)
      }

      return {
        pageBuilder: [BASE_SECTION_COLLECTION_SCHEMA],
        ...BASE_PAGE_PROPS,
      }
    })()

  if (!pageProps) {
    withScope(function (scope) {
      scope.setTag('sampleRate', 'medium')
      captureException(
        new Error(`Empty pageProps for ${ROUTE_BASE_COLLECTIONS}/${handle}`),
      )
    })
    return {
      notFound: true,
      revalidate: REVALIDATE_NOT_FOUND,
    }
  }

  const siteConfigurationRes = await getSiteConfiguration(locale, preview)

  if (siteConfigurationRes.status === 'ERROR') {
    const exception = new Error(
      `[PLP] Sanity getSiteConfiguration function error ${JSON.stringify(
        siteConfigurationRes.errors,
      )}`,
    )
    captureException(exception)
    throw exception
  }

  const { data: siteConfiguration } = siteConfigurationRes

  return {
    props: {
      ...pageProps,
      siteConfiguration,
    },
    revalidate: REVALIDATE_COLLECTION,
  }
}

export default DefaultPage
